import { LORRY_BODY_TYPE, LORRY_CATEGORIES_ENUM, VehicleInfo } from "@vahak/core/dist/constants/lorryTypes";
import { PlaceSearchResult } from "@vahak/core/dist/custom-types/location-search";
import { CompanyRouteType } from "@vahak/core/dist/custom-types/user";
import { generateFormFieldNames } from "../../../utils/form";
import { LORRY_STATUS } from "@vahak/core/dist/constants/lorryStatus";
import { AnyDestinationOption } from "../lorry-posting/lorry-posting-form-fields/vehicle-route/SelectedRouterInfo";

// Lorry posting
export enum LORRY_POSTING_FORM_LABELS {
    VEHICLE_DETAILS = "Vehicle Details",
    VEHICLE_TYPE = "Vehicle Type",
    VEHICLE_ROUTES_SELECTION = "Vehicle Routes",
    LORRY_POSTED_SUCCESSFULLY = "Lorry Posted Successfully"
}

export enum LORRY_CREATION_FORM_IDS {
    VEHICLE_NUMBER = "vehicle_number", // Enter Lorry Number Step
    VERIFY_VEHICLE = "verify_vehicle", // Lorry Details confirmation
    VERIFY_VEHICLE_FAILED = "verify_vehicle_failed", // BE not found any details
    VEHICLE_TYPE = "vehicle_type", // Body Type Selection // new repost entry point
    VEHICLE_ROUTES_SELECTION = "vehicle_routes_selection", // new step to input lorry destination
    LORRY_POSTED_SUCCESSFULLY = "lorry_posted_successfully"
}

export enum LORRY_CREATION_FORM_GROUP {
    VEHICLE_DETAILS = "Vehicle Details"
}

export interface verifyVehicleFlag {
    yesThisIsMyVehicle?: boolean;
    manualVerification?: boolean;
    showCapacityField?: boolean;
}

export interface lorryFieldType {
    vehicleBody: number | null; // used only for internal purpose
    lorryType: number;
    isLorryCapacityRequired: boolean;
    lorryCapacity: number;
    lorryTyres: number;
    lorrySize: number;
    lorrySizeNoPreference: boolean;
    lorryBodyType: LORRY_BODY_TYPE | null;
    sizeText: string;
    showCapacityField?: boolean;
    formSource?: "load" | "lorry";
    vehicleCategory?: string;

    // No need to validate but to be used to validate
    allowedCapacityMin: number | undefined;
    allowedCapacityMax: number | undefined;
    allowedVehicleList: VehicleInfo[] | undefined;
}

export interface PriceDestination {
    price?: number;
    place: PlaceSearchResult;
}

export interface lorryCreationFormFieldsTypes extends lorryFieldType, verifyVehicleFlag {
    lorryNum: string;
    currentCityPlaceId: string;
    currentCityText: string;
    currentCityOption: PlaceSearchResult | undefined;
    routeIds: CompanyRouteType[];
    rcDocument: File | undefined;
    status?: LORRY_STATUS;
    rcExpired?: boolean;
    isRcUploadRequired?: boolean;
    formType?: "post" | "edit";
    priceDestinations?: PriceDestination[];
}

export interface editBulkLoadLorryDetails extends lorryFieldType {
    id: number;
    lorryTableId: number;
    rcExpired?: boolean;
}

/** Default tyre count for LCV when tyre selection is not shown. */
export const LCV_DEFAULT_TYRE_COUNT = 4;

/** LCV tyre UI is removed — load APIs still expect total_tyres (default 4). */
export function resolveLoadPostTotalTyres(lorryType?: number | null, lorryTyres?: number | null): number {
    if (lorryType === LORRY_CATEGORIES_ENUM.LCV) {
        return lorryTyres && lorryTyres > 0 ? lorryTyres : LCV_DEFAULT_TYRE_COUNT;
    }
    return lorryTyres || 0;
}

export const initialLorryFieldFormValues: lorryFieldType = {
    vehicleBody: null,
    lorryType: 0,
    isLorryCapacityRequired: true,
    lorryCapacity: 0,
    lorryBodyType: null,
    lorrySize: 0,
    lorrySizeNoPreference: false,
    lorryTyres: 0,
    sizeText: "",
    showCapacityField: false,
    allowedCapacityMin: undefined,
    allowedCapacityMax: undefined,
    allowedVehicleList: undefined
};

export const commonLorryFormFieldNames = generateFormFieldNames(initialLorryFieldFormValues);

export const initialLorryCreationFormValues: lorryCreationFormFieldsTypes = {
    lorryNum: "",
    currentCityPlaceId: "",
    currentCityText: "",
    currentCityOption: undefined,
    routeIds: [],
    rcDocument: undefined,
    rcExpired: false,
    isRcUploadRequired: false,
    priceDestinations: [AnyDestinationOption?.value],
    ...initialLorryFieldFormValues
};

export const lorryCreationFormFieldNames = generateFormFieldNames(initialLorryCreationFormValues);

// Load posting
export enum LOAD_POSTING_FORM_LABELS {
    LOAD_DETAILS = "Load Details",
    VEHICLE_TYPE = "Vehicle Details",
    PAYMENT_DETAILS = "Payment Details"
}

export enum LOAD_CREATION_FORM_IDS {
    LOAD_LOCATION_DETAILS = "load_locations_details",
    LOAD_INFO = "load_info",
    VEHICLE_TYPE = "vehicle_type",
    PAYMENT_DETAILS = "payment_details",
    ADVANCE_AMOUNT = "advance_amount",
    LOAD_POSTED_SUCCESSFULLY = "load_post_success"
}

export enum LOAD_CREATION_FORM_GROUP {
    LOAD_DETAILS = "Location & Material details",
    PAYMENT_DETAILS = "Payment Details"
}

interface loadCreationFormMetaInfo {
    isExpectedAmountMandatory?: boolean;
    postedTime: string | undefined;
    savedSrcPlaceInfo: PlaceSearchResult | undefined;
    savedDestPlaceInfo: PlaceSearchResult | undefined;
    minExpectedPrice?: number | undefined;
    // recommendedPriceMin?: number | undefined;
    // recommendedPriceMax?: number | undefined;
    savedWaypointsPlaceInfo: (PlaceSearchResult | undefined)[] | undefined;
    stopPoints: [string] | undefined;
    rcExpired?: boolean;
    selectedLorryType?: VehicleInfo;
    isSpotLoad?: boolean;
    isTapNgoLoad?: boolean;
    loadGhostingPenaltyDeduction?: boolean;
    showLoadGhostingPaymentBanner: boolean;
}

export interface loadCreationFormFieldsTypes extends lorryFieldType, loadCreationFormMetaInfo {
    srcPlaceId: string;
    destPlaceId: string;
    srcText: string;
    destText: string;
    material: string;
    quantity: number | undefined;
    quantityType: string;
    expectedAmount: number | undefined;
    isNegotiable: boolean;
    paymentType: 0 | 1 | undefined;
    isFixedPrice: boolean;
    advanceAmount: number | undefined;
    remarks: string;
    is_odc: boolean;
    length: number | undefined;
    breadth: number | undefined;
    height: number | undefined;
    loadingTimestamp: number | undefined;
    source_id: number | undefined;
    destination_id: number | undefined;
    loadOwnerCompanyId: number;
}

export const initialLoadCreationFormValues: loadCreationFormFieldsTypes = {
    ...initialLorryFieldFormValues,
    srcPlaceId: "",
    destPlaceId: "",
    srcText: "",
    destText: "",
    material: "",
    quantity: undefined,
    quantityType: "Tonne(s)",
    expectedAmount: undefined,
    isNegotiable: false,
    paymentType: 1,
    isFixedPrice: true,
    advanceAmount: undefined,
    remarks: "",
    is_odc: false,
    length: undefined,
    breadth: undefined,
    height: undefined,
    loadingTimestamp: undefined,
    postedTime: undefined,
    isExpectedAmountMandatory: true,
    savedDestPlaceInfo: undefined,
    savedSrcPlaceInfo: undefined,
    minExpectedPrice: undefined,
    // recommendedPriceMin: undefined,
    // recommendedPriceMax: undefined,
    rcExpired: false,
    isLorryCapacityRequired: false,
    source_id: undefined,
    destination_id: undefined,
    isSpotLoad: false,
    isTapNgoLoad: false,
    loadGhostingPenaltyDeduction: false,
    showLoadGhostingPaymentBanner: false,
    savedWaypointsPlaceInfo: undefined,
    stopPoints: undefined,
    selectedLorryType: undefined,
    loadOwnerCompanyId: 0
};

export const loadCreationFormFieldNames = generateFormFieldNames(initialLoadCreationFormValues);

export interface EditLorryTypes {
    values: lorryCreationFormFieldsTypes;
    id: number;
}
export interface EditLoadTypes {
    values: loadCreationFormFieldsTypes;
    id: number;
}
